import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BellRing, Mail, Send, CheckCircle2, AlertTriangle, Cog } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function fmtIst(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", " ·") + " IST";
}

export default function NotificationsSettings() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/email-config"); setCfg(data); setTestTo((prev) => prev || data.from_email || ""); };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Recipient email is required"); return; }
    setBusy(true); setLastResult(null);
    try {
      const { data } = await api.post("/admin/email-config/test", { to: testTo.trim() });
      setLastResult(data);
      if (data.ok) toast.success("Test email sent"); else toast.error("Test failed", { description: data.message });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    setBusy(false);
  };

  if (!cfg) return <div className="p-8 text-slate-500">Loading…</div>;

  const active = cfg.enabled && cfg.smtp_password_set && cfg.smtp_host && cfg.from_email;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><BellRing className="h-3.5 w-3.5" /> Alerts</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Notifications</h1>
          <p className="text-slate-500 text-[13px] mt-1">SMTP diagnostics for Ops IQ. Use this page to verify that the configured mail server can actually deliver.</p>
        </div>
        <Button variant="outline" onClick={() => nav("/admin/email-configuration")} data-testid="open-email-config-btn"><Cog className="h-4 w-4 mr-2" /> Open Email Configuration</Button>
      </div>

      {/* Provider status card */}
      <Card className={`p-5 border rounded-md ${active ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="smtp-status-card">
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-md flex items-center justify-center ${active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
            <Mail className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-heading font-bold text-lg">Email ({cfg.provider === "smtp" ? "SMTP" : cfg.provider})</div>
              {active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold border border-emerald-200"><CheckCircle2 className="h-3 w-3" /> Active</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[11px] font-semibold border border-amber-200"><AlertTriangle className="h-3 w-3" /> Not configured</span>
              )}
            </div>
            <div className="text-[12.5px] text-slate-600 mt-1 font-mono">
              From: {cfg.from_email || "—"} · Relay: {cfg.smtp_host || "—"}:{cfg.smtp_port || "—"} {cfg.use_tls ? "(TLS)" : ""}
            </div>
            {cfg.last_test_at && (
              <div className={`mt-3 text-[12px] rounded-md px-3 py-2 border ${cfg.last_test_status === "ok" ? "bg-emerald-100/50 border-emerald-200 text-emerald-900" : "bg-rose-100/50 border-rose-200 text-rose-900"}`}>
                <div><b>Last test:</b> {fmtIst(cfg.last_test_at)} · by {cfg.last_test_by} · <b>{cfg.last_test_status?.toUpperCase()}</b></div>
                <div className="font-mono text-[11.5px] mt-0.5">{cfg.last_test_message}</div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Send test */}
      <Card className="p-5 border-slate-200 rounded-md bg-white" data-testid="send-test-card">
        <div className="flex items-center gap-2 mb-3"><Send className="h-4 w-4 text-brand" /> <div className="font-heading font-bold text-lg">Send Test Email</div></div>
        <p className="text-[12.5px] text-slate-500 mb-3">Fires a real email through the configured SMTP relay using sender <span className="font-mono">{cfg.from_email || "(not set)"}</span>. Use this to diagnose why "Active" isn't translating into real delivery — the exact SMTP response is shown below.</p>
        <div className="flex items-center gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@bbsglobal.in" data-testid="test-to-input" className="max-w-sm font-mono" />
          <Button onClick={sendTest} disabled={busy || !cfg.enabled} className="bg-brand hover:bg-brand-dark text-white" data-testid="send-test-btn"><Send className="h-4 w-4 mr-2" /> {busy ? "Sending…" : "Send Test"}</Button>
        </div>
        {!cfg.enabled && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="disabled-hint">
            Email delivery is currently disabled. Toggle Enabled ON in <button onClick={() => nav("/admin/email-configuration")} className="underline font-semibold">Email Configuration</button> before sending.
          </div>
        )}
        {lastResult && (
          <div className={`mt-3 rounded-md border px-3 py-2 text-[12px] font-mono ${lastResult.ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"}`} data-testid="test-result">
            {lastResult.ok ? "✓ " : "✗ "}{lastResult.message}
          </div>
        )}
      </Card>
    </div>
  );
}
